import {
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AssetSearch, BatteryLevelsEnum } from '@/types/asset_search';
import {
  container,
  cardContainer,
  cardContent,
  closeIcon,
  optionTitle,
  optionValue,
} from './mapOverlay.style';
import { VehicleBatteryBadge } from '../VehicleBatteryBadge';
import { useTranslation } from 'react-i18next';
import { BackIcon, CheckIcon, InfoIcon } from '../Icons';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setAssetMarkedAsFound } from '@/features/filters/filtersSlice';
import { Dispatch, SetStateAction, useState } from 'react';
import { Action, Subject } from '@/types/enums';
import Can from '@/app/Auth/Can';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs, { extend } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

extend(minMax);

interface IProps {
  asset: AssetSearch | null;
  setSelectedAsset: Dispatch<SetStateAction<AssetSearch | null>>;
  onClose?: () => void;
  handlePreviousOrNextAsset: (action: 'previous' | 'next') => void;
}

export const MapOverlay = ({
  asset,
  setSelectedAsset,
  onClose,
  handlePreviousOrNextAsset,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme<Theme>();
  const dispatch = useAppDispatch();

  const nameSite = useAppSelector((state) => state.user.siteMetadata.name);

  const isDesktop = useMediaQuery('(min-width:900px)');

  const [dtcExpanded, setDtcExpanded] = useState<boolean>(false);
  const [dtcTooltip, setDtcTooltip] = useState<boolean>(false);

  const dtcDates: dayjs.Dayjs[] = [];
  let latestScannedDtcDate;

  if (
    Can(Action.READ, Subject.table_dtc) &&
    Array.isArray(asset?.dtc) &&
    asset?.dtc &&
    asset.dtc?.length > 0
  ) {
    asset.dtc.map((dtc) => {
      dtcDates.push(dayjs(dtc.scanned_date));
    });
    latestScannedDtcDate = dayjs.max(dtcDates);
  }

  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(() => ({
    paddingTop: 0,
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
      data-testid="map-overlay-accordion-summary"
      expandIcon={
        <ExpandMoreIcon
          sx={{ color: theme.palette?.low?.contrastText }}
          onClick={() => setDtcExpanded(!dtcExpanded)}
        />
      }
      {...props}
    />
  ))(() => ({
    margin: 0,
    backgroundColor: theme.palette?.low?.light,
    height: '40px',
  }));

  return (
    <Box sx={() => container}>
      <Card variant="outlined" sx={cardContainer}>
        <CardContent sx={cardContent}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              padding: '0 8px!important',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => handlePreviousOrNextAsset('previous')}
                data-testid="map-overlay-previous-asset"
                sx={{ background: '#F0F2F5', width: '28px', height: '28px' }}
              >
                <BackIcon
                  viewBox="0 0 8 14"
                  style={{ width: '8px', height: '14px' }}
                />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  marginLeft: '10px',
                }}
              >
                <Box>
                  {(asset?.vehicle_make || asset?.vehicle_model) && (
                    <Typography
                      sx={{ fontSize: '14px', fontWeight: '600' }}
                      data-testid="vehicle-make-model"
                      component="p"
                    >
                      {asset?.vehicle_make}
                      {asset?.vehicle_make && asset?.vehicle_model && '-'}
                      {asset?.vehicle_model}
                    </Typography>
                  )}
                </Box>
                <Box>
                  {(asset?.vehicle_year || asset?.vehicle_color) && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '400',
                          whiteSpace: 'nowrap',
                        }}
                        data-testid="vehicle-year-color"
                        component="p"
                      >
                        {asset?.vehicle_year}
                        {asset?.vehicle_year &&
                          asset?.vehicle_color &&
                          ','}{' '}
                        {asset?.vehicle_color}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={onClose}
                sx={closeIcon}
                data-testid="map-overlay-close-icon"
              >
                <CloseIcon />
              </IconButton>
              <IconButton
                onClick={() => handlePreviousOrNextAsset('next')}
                data-testid="map-overlay-next-asset"
                sx={{
                  marginLeft: '10px',
                  background: '#F0F2F5',
                  width: '28px',
                  height: '28px',
                }}
              >
                <BackIcon
                  viewBox="0 0 8 14"
                  style={{ width: '8px', height: '14px', rotate: '180deg' }}
                  data-testid="previous-asset-button"
                />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flex: 1,
              marginBottom: '10px',
              padding: '0 8px!important',
            }}
          >
            <Typography sx={optionValue} data-testid="map-overlay-asset_uuid">
              {asset?.asset_uuid}
            </Typography>

            {Can(Action.WRITE, Subject.mark_as_found) && (
              <Button
                data-testid="mark-as-found-button"
                sx={(theme) => ({
                  padding: '6px 8px',
                  borderRadius: '80px',
                  border: '1px solid',
                  borderColor: asset?.markedAsFound
                    ? theme.palette.primary.main
                    : '#BCC2CC',
                  backgroundColor: asset?.markedAsFound
                    ? theme.palette.mode === 'light'
                      ? theme.palette?.brand200?.main
                      : `${theme.palette.primary.main}10`
                    : 'transparent',
                  marginLeft: '12px',
                })}
                onClick={() => {
                  if (asset) {
                    dispatch(
                      setAssetMarkedAsFound({
                        assetUuid: asset?.asset_uuid,
                        checked: !asset?.markedAsFound,
                      }),
                    );
                    setSelectedAsset({
                      ...asset,
                      markedAsFound: !asset?.markedAsFound,
                    });
                  }
                }}
              >
                {asset?.markedAsFound && (
                  <CheckIcon
                    viewBox="0 0 14 12"
                    style={{ width: '16px', height: '16px' }}
                  />
                )}
                <Typography
                  sx={(theme) => ({
                    color: asset?.markedAsFound
                      ? theme.palette.mode === 'light'
                        ? theme.palette?.brand800?.main
                        : theme.palette?.brand300?.main
                      : theme.palette.text.primary,

                    textTransform: 'capitalize',
                    fontSize: '14px',
                    marginLeft: asset?.markedAsFound ? '5px' : 0,
                    lineHeight: '16px',
                  })}
                >
                  {asset?.markedAsFound
                    ? t('mapOverlay.found')
                    : t('mapOverlay.markAsFound')}
                </Typography>
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              marginBottom: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '0 8px!important',
            }}
          >
            {asset?.zone_name && (
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  padding: '2px',
                  display: 'flex',
                  justifyCenter: 'center',
                  alignItems: 'center',
                })}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    whiteSpace: 'nowrap',
                  }}
                  component="p"
                  data-testid="map-overlay-zone"
                >
                  {asset?.zone_name}
                </Typography>
              </Box>
            )}
            {asset?.work_order && (
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  padding: '2px',
                  display: 'flex',
                  justifyCenter: 'center',
                  alignItems: 'center',
                })}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    whiteSpace: 'nowrap',
                  }}
                  data-testid="map-overlay-workorder"
                  component="p"
                >
                  WO # {asset?.work_order}
                </Typography>
              </Box>
            )}
            {(asset?.sale_year != null ||
              asset?.sale_number != null ||
              asset?.sale_lane != null ||
              asset?.sale_run != null) && (
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  padding: '2px',
                  display: 'flex',
                  justifyCenter: 'center',
                  alignItems: 'center',
                })}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    whiteSpace: 'nowrap',
                  }}
                  data-testid="vehicle-sale-value"
                  component="p"
                >
                  {asset?.sale_year} {' - '} {asset?.sale_number} {' - '}
                  {asset?.sale_lane} {' - '} {asset?.sale_run}
                </Typography>
              </Box>
            )}

            {asset?.vehicle_level && (
              <VehicleBatteryBadge
                batteryLevel={asset?.vehicle_level as BatteryLevelsEnum}
              />
            )}
          </Box>

          {Can(Action.READ, Subject.table_dtc) &&
            (asset?.has_dtc_optedout ? (
              <Box
                data-testid="has-dtc-otpedout-box"
                sx={{
                  padding: '10px 8px',
                  backgroundColor: '#ffc879',
                  display: 'flex',
                  justifyContent: 'left',
                  alignItems: 'left',
                  textAlign: 'left',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'black',
                  }}
                >
                  {t(`mapOverlay.dtcSellerOptedOut`)}
                </Typography>
              </Box>
            ) : (
              Array.isArray(asset?.dtc) &&
              asset?.dtc &&
              asset.dtc?.length > 0 && (
                <Accordion
                  expanded={dtcExpanded}
                  sx={{
                    width: {
                      xs: '100%',
                      md: 'auto',
                    },
                    background: 'transparent',
                    textAlign: 'left',
                    '&:before': {
                      display: 'none',
                    },
                  }}
                  data-testid="map-overlay-dtc-accordion"
                >
                  <AccordionSummary
                    data-testid="map-overlay-accordion-summary"
                    sx={() => ({
                      background: '#FFD4BF',
                      padding: 0,
                      display: 'flex',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center!important',
                      },
                    })}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: '600',
                        padding: '0 8px!important',
                        color: theme.palette?.low?.contrastText,
                        height: '100%',
                      }}
                    >
                      {t('mapOverlay.dtcInformation')}
                    </Typography>
                    {Can(Action.READ, Subject.disclaimer) && (
                      <Tooltip
                        data-testid="dtc-disclaimer"
                        open={dtcTooltip}
                        onMouseEnter={() => setDtcTooltip(true)}
                        onMouseLeave={() => setDtcTooltip(false)}
                        onClick={() => !isDesktop && setDtcTooltip(!dtcTooltip)}
                        title={
                          <>
                            <Typography
                              sx={() => ({
                                fontSize: '14px',
                                fontWeight: '600',
                              })}
                            >
                              {t('disclaimerDialog.dtcDisclaimerTitle')}
                              <br />
                              <br />
                            </Typography>
                            <Typography
                              sx={() => ({
                                fontSize: '14px',
                              })}
                            >
                              {t('disclaimerDialog.dtcDisclaimer', {
                                SITE_ID: nameSite,
                              })}
                              <br />
                              <br />
                            </Typography>
                          </>
                        }
                      >
                        <span>
                          <InfoIcon
                            style={{
                              width: '15px',
                              height: '15px',
                              marginLeft: '5px',
                            }}
                            viewBox="0 0 20 20"
                          />
                        </span>
                      </Tooltip>
                    )}
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: theme.palette?.low?.lightest,
                      padding: '8px',
                      overflowY: 'scroll',
                      maxHeight: '150px',
                    }}
                  >
                    <Box sx={{ padding: '12px 0' }}>
                      <Typography
                        sx={{ fontSize: 14, padding: 0 }}
                        data-testid="map-overlay-dtc-last-scanned"
                      >
                        DTC Scanned{' '}
                        {latestScannedDtcDate
                          ? new Date(`${latestScannedDtcDate}`).toLocaleString()
                          : ''}
                      </Typography>
                    </Box>
                    {asset?.dtc &&
                      asset.dtc?.map((dtc) => (
                        <Box
                          key={dtc.code}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 0',
                            '&:nth-of-type(odd)': {
                              background:
                                theme.palette.mode === 'light'
                                  ? theme.palette.primary.light
                                  : theme.palette.grey[50],
                            },
                            '&:nth-of-type(even)': {
                              background: theme.palette.elevation01?.main,
                            },
                          }}
                        >
                          <Box>
                            <Typography
                              sx={optionTitle}
                              data-testid="map-overlay-dtc-code"
                            >
                              {dtc.code}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 14, padding: 0 }}
                              data-testid="map-overlay-dtc-description"
                            >
                              {dtc.description}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-end',
                              alignItems: 'flex-end',
                            }}
                          >
                            {dtc.status.map((status) => (
                              <Typography
                                key={status}
                                data-testid={`map-overlay-dtc-status-${status}`}
                                sx={{
                                  textTransform: 'lowercase',
                                  backgroundColor: theme.palette?.divider,
                                  fontSize: '12px',
                                  padding: '2px 4px',
                                  borderRadius: '30px',
                                  marginBottom: '2px',
                                }}
                              >
                                {status}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ))}
                  </AccordionDetails>
                </Accordion>
              )
            ))}
        </CardContent>
      </Card>
    </Box>
  );
};
